import { DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { CommonDialog } from "../../../components/common/CommonDialog";
import {
  FormAutoComplete,
  FormButtonGroup,
  FormDateInput,
  FormMultipleAutoComplete,
  FormTextInput,
} from "../../../components/form";
import { oids } from "../../../configs/Settings";
import { useOrganizationContext } from "../../../contexts/OrganizationContext";
import { usePractitionerRoleContext } from "../../../contexts/PractitionerRoleContext";
import { useSessionContext } from "../../../contexts/SessionContext";
import { SendEmail } from "../../../services/gmail";
import {
  GetPractitioner,
  PutPractitioner,
  PutPractitionerRole,
  Validate,
} from "../../../services/fhir";
import { SearchCPF, toSerproNascimento } from "../../../services/scim";
import { HTTPResponseStatus } from "../../../utils/enum";
import { useFormatter } from "../../../utils/useFormatter";
import { useRegex } from "../../../utils/useRegex";

type AssociationOption = {
  cnpjDigits: string;
  organizationAlias: string;
};

interface IProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  practitioners: any;
  associationOptions?: AssociationOption[];
  defaultAssociationCnpjDigits?: string;
}

const defaultValues = {
  email: "",
  identifier: "",
  birthDate: null as any,
  phone: "",
  associationCnpj: null as any,
  role: [] as any[],
};

function onlyDigits(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\D/g, "");
}

function isFhirOrganization(value: unknown): value is fhir5.Organization {
  if (!value || typeof value !== "object") return false;
  return (value as { resourceType?: unknown }).resourceType === "Organization";
}

export function InviteDialog({
  isOpen,
  setIsOpen,
  practitioners,
  associationOptions,
  defaultAssociationCnpjDigits,
}: IProps) {
  const { organization } = useOrganizationContext();
  const { refetchPractitionerRole } = usePractitionerRoleContext();

  const [loading, setLoading] = useState(false);
  const methods = useForm({
    defaultValues: {
      ...defaultValues,
      associationCnpj: defaultAssociationCnpjDigits
        ? {
            value: onlyDigits(defaultAssociationCnpjDigits),
            label: useFormatter.formatCNPJ(
              onlyDigits(defaultAssociationCnpjDigits),
            ),
          }
        : null,
    },
  });
  const { access } = useSessionContext();

  const { handleSubmit, control, reset, setValue, watch } = methods;

  const watchedAssociation = useWatch({ control, name: "associationCnpj" });

  // Com CNPJ de associação no convite, não permitimos papel de proprietário (regra de negócio).
  const associationCnpjForRules = useMemo(() => {
    if (defaultAssociationCnpjDigits) {
      return onlyDigits(defaultAssociationCnpjDigits);
    }
    const raw =
      typeof watchedAssociation === "string"
        ? watchedAssociation
        : watchedAssociation?.value;
    return onlyDigits(raw);
  }, [defaultAssociationCnpjDigits, watchedAssociation]);

  const hasAssociationCnpj = Boolean(associationCnpjForRules);

  const roleSelectOptions = useMemo(() => {
    const base = [
      { label: "Proprietário", value: "owner" },
      { label: "Secretariado", value: "manager" },
      { label: "Profissional de saúde", value: "practitioner" },
    ];
    const filtered = hasAssociationCnpj
      ? base.filter((o) => o.value !== "owner")
      : base;
    return filtered.sort((el: any, val: any) => (el.label < val.label ? 1 : -1));
  }, [hasAssociationCnpj]);

  const watchedRoles = watch("role");
  useEffect(() => {
    if (!hasAssociationCnpj || !Array.isArray(watchedRoles)) return;
    const hasOwner = watchedRoles.some((r: any) => r?.value === "owner");
    if (hasOwner) {
      setValue(
        "role",
        watchedRoles.filter((r: any) => r?.value !== "owner"),
        { shouldDirty: true },
      );
    }
  }, [hasAssociationCnpj, watchedRoles, setValue]);

  // const rolesOptions = [
  //   { display: "Profissional de saúde", value: "practitioner" },
  //   { display: "Secretariado", value: "manager" },
  // ];

  function transformData(inputData: string) {
    const day = inputData.substring(0, 2);
    const month = inputData.substring(2, 4);
    const year = inputData.substring(4, 8);

    const formatedDate = [year, month, day]?.join("-");

    return formatedDate;
  }

  const handleClose = () => {
    setIsOpen(false);
    reset(defaultValues);
  };

  const onSubmit = async (data: any) => {
    const practitionerExists = practitioners?.some((el: any) =>
      useFormatter.formatCPF(el?.resource?.id.split("-")[2]) === data.identifier
        ? true
        : false,
    );

    if (!practitionerExists) {
      setLoading(true);
      try {
        const invitedCpfDigits = onlyDigits(data?.identifier);

        // Associação opcional:
        // - Se digitou um CNPJ (freeSolo), validamos se o CPF convidado é sócio.
        // - Se selecionou um CNPJ existente, vinculamos sem validar (isSocio=false).
        const associationCnpjRaw =
          typeof data?.associationCnpj === "string"
            ? data.associationCnpj
            : data?.associationCnpj?.value;
        const associationCnpjDigits = onlyDigits(associationCnpjRaw);
        const associationIsNewCnpj = Boolean(
          associationCnpjDigits &&
          !associationOptions?.some(
            (o) => onlyDigits(o?.cnpjDigits) === associationCnpjDigits,
          ),
        );

        const roleValues: string[] = data?.role?.map((el: any) => el?.value) || [];
        if (associationCnpjDigits && roleValues.includes("owner")) {
          toast.error(
            "Convites vinculados a um CNPJ não podem incluir a função de proprietário.",
          );
          return;
        }

        let associationOrganizationAlias = "";
        let associationIsSocio = false;

        if (associationCnpjDigits) {
          if (associationIsNewCnpj) {
            const validation = await Validate({
              cpf: invitedCpfDigits,
              cnpj: associationCnpjDigits,
            });

            if (validation?.status !== HTTPResponseStatus.Created) {
              toast.error(
                validation?.data || "Não foi possível validar o CNPJ.",
              );
              return;
            }

            associationIsSocio = true;
            const validationData: unknown = validation?.data;
            if (isFhirOrganization(validationData)) {
              associationOrganizationAlias =
                validationData?.alias?.[0] || validationData?.name || "";
            } else {
              // Fallback: não-FHIR. Usamos um alias mínimo e estável.
              associationOrganizationAlias = associationCnpjDigits;
            }
          } else {
            associationIsSocio = false;
            const option = associationOptions?.find(
              (o) => onlyDigits(o?.cnpjDigits) === associationCnpjDigits,
            );
            associationOrganizationAlias =
              option?.organizationAlias || associationCnpjDigits;
          }
        }

        const verifyPracitioner = await GetPractitioner(invitedCpfDigits);
        if (verifyPracitioner) {
          await PutPractitionerRole({
            organizationId: `${oids.cnpj}-${access.organizationId}`,
            organizationDisplay: organization?.name || "",
            practitionerId: verifyPracitioner.id,
            practitionerDisplay: verifyPracitioner?.name?.[0].text,
            role: data?.role?.map((el: any) => el?.value),
            invite: true,
            ...(associationCnpjDigits &&
              associationOrganizationAlias && {
                associationCnpjDigits,
                associationOrganizationAlias,
                associationIsSocio,
              }),
          });
        } else {
          const nascimento = toSerproNascimento(data?.birthDate);
          if (!nascimento) {
            toast.warn("Informe a data de nascimento para validar o CPF.");
            return;
          }
          const invitedData = await SearchCPF(invitedCpfDigits, nascimento);
          if (invitedData?.data?.situacao?.descricao === "Regular") {
            const createdPractitioner = await PutPractitioner({
              identifier: invitedCpfDigits,
              name: invitedData?.data?.nome,
              birthDate: transformData(invitedData?.data?.nascimento),
            });
            await PutPractitionerRole({
              organizationId: `${oids.cnpj}-${access.organizationId}`,
              organizationDisplay:
                organization?.alias?.[0] || organization?.name || "",
              practitionerId: createdPractitioner.id,
              practitionerDisplay: invitedData?.data?.nome,
              role: data?.role?.map((el: any) => el?.value),
              invite: true,
              ...(associationCnpjDigits &&
                associationOrganizationAlias && {
                  associationCnpjDigits,
                  associationOrganizationAlias,
                  associationIsSocio,
                }),
            });
          } else throw new Error("CPF não encontrado ou situação irregular");
        }

        toast.success(
          `Convite enviado com sucesso para o profissional de CPF: ${data?.identifier}`,
        );

        refetchPractitionerRole();
        handleClose();
        await SendEmail(
          { email: data.contact },
          data.inviteMessage || "",
          organization?.name,
        );
      } catch (err) {
        console.log("err", err);
        toast.info(
          `Atualize a página e verifique se o CPF adicionado consta na aba de convidados`,
        );
      } finally {
        setLoading(false);
      }
    } else {
      handleClose();
      toast.warning("CPF já cadastrado ou aguardando confirmação!");
    }
  };

  return (
    <CommonDialog handleClose={handleClose} isOpen={isOpen}>
      <DialogTitle textAlign="center"> Convidar profissional </DialogTitle>
      <DialogContent>
        <Stack
          width="100%"
          height="100%"
          flex="1"
          justifyContent="center"
          alignItems="center"
          spacing={4}
        >
          <Typography
            variant="body1"
            color="neutral700.main"
            textAlign="center"
            m={{ xs: 2, md: 5 }}
          >
            Informe os dados do profissional que deseja convidar para sua
            clínica
          </Typography>

          <FormTextInput
            required
            name="identifier"
            control={control}
            label="CPF"
            maxLength={14}
            pattern={useRegex.cpf}
            mask={useFormatter.formatCPF}
            autoFocus
          />

          <FormDateInput
            name="birthDate"
            control={control}
            label="Data de nascimento"
          />

          {/* <Stack direction="row" width="100%" spacing={2}>
            <FormAutoComplete
              name="associationCnpj"
              control={control}
              label={
                defaultAssociationCnpjDigits
                  ? "CNPJ selecionado"
                  : "CNPJ (opcional - selecione ou digite)"
              }
              required={false}
              freeSolo={!defaultAssociationCnpjDigits}
              disabled={Boolean(defaultAssociationCnpjDigits)}
              noOptionsText="Digite um CNPJ completo"
              selectOptions={
                Array.isArray(associationOptions)
                  ? associationOptions.map((o) => ({
                      value: onlyDigits(o.cnpjDigits),
                      label: `${o.organizationAlias} (${useFormatter.formatCNPJ(
                        onlyDigits(o.cnpjDigits),
                      )})`,
                    }))
                  : []
              }
            />
          </Stack> */}

          <Stack direction="row" width="100%" spacing={2}>
            <FormTextInput
              required
              type="email"
              name="contact"
              control={control}
              pattern={useRegex.email}
              label="Email"
              autoFocus
            />
          </Stack>

          <Stack direction="row" width="100%" spacing={2}>
            <FormMultipleAutoComplete
              required
              variant="filled"
              control={control}
              // defaultValue={row?.paper}
              label="Função"
              name="role"
              selectOptions={roleSelectOptions}
            />
          </Stack>

          <Stack direction="row" width="100%" spacing={2}>
            <FormTextInput
              multiline={true}
              rows={6}
              name="inviteMessage"
              control={control}
              label="Mensagem"
              autoFocus
            />
          </Stack>
        </Stack>

        <Stack
          minWidth="100%"
          mt={3}
          direction="row"
          justifyContent="space-between"
          spacing={2}
        >
          <FormButtonGroup
            onGoBackButtonClick={handleClose}
            onNextButtonClick={handleSubmit(onSubmit)}
            goBackButtonText="Voltar"
            nextButtonText="Confirmar"
            loading={loading}
          />
        </Stack>
      </DialogContent>
    </CommonDialog>
  );
}
